import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, QrCode, Info, RefreshCw } from "lucide-react";
import { API } from "@/context/AuthContext";

// Gate-In: pick open PO lines, enter qty received, generate labels before the GRN is posted.
export function GateInPanel({ perUnit, pieceRange, onLabels, loading, setLoading }) {
  const [po, setPo] = useState("");
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({}); // item -> qty string ("" = not selected)

  const fetchPo = async (refresh = false) => {
    if (!po.trim()) { toast.error("Enter a Purchase Order Number"); return; }
    setLoading(true); onLabels(null);
    try {
      const r = await axios.get(`${API}/inventory/po-open-items`, { params: { po: po.trim(), ...(refresh ? { refresh: true } : {}) } });
      setData(r.data);
      const init = {};
      (r.data.items || []).forEach((it) => { init[it.item] = ""; });
      setSel(init);
      if (!(r.data.items || []).length) toast.warning((r.data.errors && r.data.errors[0]) || "No lines found on this PO");
      else toast.success(`${r.data.items.length} PO line(s) loaded${r.data.source === "demo" ? " (demo data)" : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not fetch the Purchase Order"); }
    finally { setLoading(false); }
  };

  const chosen = Object.entries(sel).filter(([, q]) => q !== "" && Number(q) > 0).map(([item, q]) => ({ item, qty_received: Number(q) }));

  const generate = async () => {
    if (!chosen.length) { toast.error("Enter the received quantity for at least one line"); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/inventory/gate-in-labels`, { po: po.trim(), lines: chosen, per_unit: perUnit, register: true, ...pieceRange });
      onLabels({ ...r.data, po: po.trim(), lines: chosen });
      (r.data.warnings || []).forEach((w) => toast.warning(w));
      toast.success(`${r.data.label_count} Gate-In label(s) generated · saved to Gate-In Register`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate labels"); }
    finally { setLoading(false); }
  };

  const items = data?.items || [];
  const bal = (it) => (it.balance_qty ?? it.open_qty);
  const openItems = items.filter((it) => bal(it) > 0);
  const gatedOut = items.filter((it) => it.open_qty > 0 && bal(it) <= 0).length;
  return (
    <>
      <div className="no-print flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="w-full sm:w-auto">
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Purchase Order No.</label>
          <Input value={po} onChange={(e) => setPo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchPo()} inputMode="numeric"
            placeholder="e.g. 4300000004" className="h-11 md:h-9 w-full sm:w-56 border-slate-300 text-base md:text-sm font-mono" data-testid="gatein-po-input" />
        </div>
        <Button onClick={() => fetchPo()} disabled={loading} data-testid="gatein-fetch-btn" className="h-11 md:h-9 flex-1 sm:flex-none bg-sky-700 hover:bg-sky-800 text-white">
          <Search className="h-4 w-4 mr-1.5" />{loading ? "Fetching…" : "Fetch open items"}
        </Button>
        {data && (
          <Button variant="ghost" size="sm" onClick={() => fetchPo(true)} disabled={loading} title="Re-read received quantities from SAP" data-testid="gatein-refresh-btn" className="text-slate-500">
            <RefreshCw className="h-4 w-4 mr-1" />Refresh
          </Button>
        )}
        {openItems.length > 0 && (
          <Button onClick={generate} disabled={loading || !chosen.length} data-testid="gatein-generate-btn" className="hidden md:inline-flex ml-auto bg-emerald-700 hover:bg-emerald-800 text-white">
            <QrCode className="h-4 w-4 mr-1.5" />Generate labels{chosen.length ? ` (${chosen.length} line${chosen.length > 1 ? "s" : ""})` : ""}
          </Button>
        )}
      </div>

      {data?.source === "demo" && (
        <div className="no-print rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2" data-testid="gatein-demo-note">
          <Info className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-800">No live SAP connection — showing <b>demo</b> PO lines so you can preview the Gate-In flow.</p>
        </div>
      )}

      {data && items.length > 0 && (
        <div className="no-print rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="gatein-items-card">
          <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap gap-x-6 gap-y-1 text-sm">
            <span className="text-slate-500">PO: <b className="text-slate-800 font-mono">{data.po}</b></span>
            {data.header?.supplier && <span className="text-slate-500">Supplier: <b className="text-slate-800">{data.header.supplier_name || data.header.supplier}</b></span>}
            {data.header?.po_date && <span className="text-slate-500">PO Date: <b className="text-slate-800">{data.header.po_date}</b></span>}
            <span className="text-slate-500">Lines to gate: <b className="text-slate-800" data-testid="gatein-open-count">{openItems.length}</b> of {items.length}
              {(items.length - openItems.length) > 0 && <span className="text-slate-400 text-xs"> ({items.length - openItems.length - gatedOut > 0 ? `${items.length - openItems.length - gatedOut} fully received` : ""}{items.length - openItems.length - gatedOut > 0 && gatedOut > 0 ? ", " : ""}{gatedOut > 0 ? `${gatedOut} fully gated — reprint from Gate-In Register` : ""} — hidden)</span>}
            </span>
            {data.gate_in_entries > 0 && <span className="text-slate-500">Gate-ins recorded: <b className="text-slate-800" data-testid="gatein-entries-count">{data.gate_in_entries}</b></span>}
          </div>
          {openItems.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-emerald-700" data-testid="gatein-all-received">
              {gatedOut > 0 ? "Every open quantity on this PO has already been gated in — reprint labels from the Gate-In Register tab." : "All quantities on this PO have already been received — nothing left to gate in."}
            </p>
          )}
          {openItems.length > 0 && (<div className="md:hidden divide-y divide-slate-100" data-testid="gatein-items-cards">
            {openItems.map((it) => {
              const q = sel[it.item] ?? "";
              const over = q !== "" && Number(q) > bal(it);
              return (
                <div key={it.item} className="p-4 space-y-2" data-testid={`gatein-card-${it.item}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-mono font-bold text-slate-900 truncate">{it.material || "—"}</p>
                      <p className="text-xs text-slate-600 line-clamp-2">{it.description}</p>
                    </div>
                    <span className="shrink-0 rounded-full bg-slate-100 text-slate-700 text-[11px] font-semibold px-2 py-0.5">Line {it.item}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 truncate"><span className="font-mono">{it.wbs_element || "—"}</span>{it.wbs_description && ` · ${it.wbs_description}`} · Plant {it.plant}</p>
                  <div className="grid grid-cols-4 gap-1 text-center text-[11px]">
                    {[["Ordered", it.ordered_qty], ["GRN", it.received_qty], ["Gated", it.pending_gated_qty ?? 0], ["Balance", bal(it)]].map(([l, v]) => (
                      <div key={l} className={`rounded-md py-1 ${l === "Balance" ? "bg-emerald-50 text-emerald-800 font-bold" : "bg-slate-50 text-slate-700"}`}><p className="text-[9px] uppercase tracking-wider text-slate-400">{l}</p>{v}</div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <Input type="number" inputMode="decimal" min="0" step="any" value={q} placeholder={`Qty received now (≤ ${bal(it)})`}
                      onChange={(e) => setSel((s) => ({ ...s, [it.item]: e.target.value }))}
                      className={`h-11 flex-1 text-base tabular-nums ${over ? "border-amber-500 bg-amber-50" : "border-slate-300"}`} data-testid={`gatein-qty-m-${it.item}`} />
                    <Button type="button" variant="outline" onClick={() => setSel((s) => ({ ...s, [it.item]: String(bal(it)) }))} className="h-11 px-3 border-slate-300 text-sky-700 font-semibold" data-testid={`gatein-fill-m-${it.item}`}>All</Button>
                  </div>
                  {over && <p className="text-[11px] text-amber-700">Exceeds the balance left to gate — will be flagged.</p>}
                </div>
              );
            })}
          </div>)}
          {openItems.length > 0 && (<div className="hidden md:block overflow-x-auto"><Table data-testid="gatein-items-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                {["Line", "Material", "Description", "WBS", "Plant", "Ordered", "GRN Recd", "Open", "Gated (await GRN)", "Balance", "Qty received now"].map((h) => (
                  <TableHead key={h} className={`text-xs uppercase tracking-wider text-slate-500 ${["Ordered", "GRN Recd", "Open", "Gated (await GRN)", "Balance", "Qty received now"].includes(h) ? "text-right" : ""}`}>{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {openItems.map((it) => {
                const open = bal(it) > 0;
                const q = sel[it.item] ?? "";
                const over = q !== "" && Number(q) > bal(it);
                return (
                  <TableRow key={it.item} className={`border-b border-slate-100 ${open ? "" : "bg-slate-50/60"}`} data-testid={`gatein-row-${it.item}`}>
                    <TableCell className="text-sm font-mono text-slate-700">{it.item}</TableCell>
                    <TableCell className="text-sm font-mono font-semibold text-slate-900">{it.material || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-[260px] truncate" title={it.description}>{it.description || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-600 max-w-[220px] truncate" title={it.wbs_description}><span className="font-mono">{it.wbs_element || "—"}</span>{it.wbs_description && <span className="text-slate-400"> · {it.wbs_description}</span>}</TableCell>
                    <TableCell className="text-sm text-slate-600">{it.plant}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums text-slate-700">{it.ordered_qty} {it.unit}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums text-slate-700">{it.received_qty}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums text-slate-700">{it.open_qty}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums text-amber-700" data-testid={`gatein-gated-${it.item}`}>{it.pending_gated_qty ?? 0}{it.gated_qty > (it.pending_gated_qty ?? 0) && <span className="text-[10px] text-slate-400"> / {it.gated_qty} total</span>}</TableCell>
                    <TableCell className={`text-right text-sm tabular-nums font-semibold ${open ? "text-emerald-700" : "text-slate-400"}`} data-testid={`gatein-balance-${it.item}`}>{bal(it)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Input type="number" min="0" step="any" value={q} placeholder={open ? `≤ ${bal(it)}` : "0 open"}
                          onChange={(e) => setSel((s) => ({ ...s, [it.item]: e.target.value }))}
                          className={`h-8 w-28 text-right text-sm tabular-nums ${over ? "border-amber-500 bg-amber-50" : "border-slate-300"}`}
                          title={over ? "Exceeds the balance left to gate on this line" : ""}
                          data-testid={`gatein-qty-${it.item}`} />
                        {open && <button type="button" onClick={() => setSel((s) => ({ ...s, [it.item]: String(bal(it)) }))}
                          className="text-[10px] font-semibold text-sky-700 hover:underline" data-testid={`gatein-fill-${it.item}`}>all</button>}
                      </div>
                      {!open && <p className="text-[10px] text-slate-400 mt-0.5">fully received — over-delivery will be flagged</p>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table></div>)}
        </div>
      )}
      {openItems.length > 0 && (
        <div className="md:hidden sticky bottom-3 z-20 no-print">
          <Button onClick={generate} disabled={loading || !chosen.length} className="w-full h-12 text-base bg-emerald-700 hover:bg-emerald-800 text-white shadow-lg" data-testid="gatein-generate-btn-m">
            <QrCode className="h-5 w-5 mr-2" />Generate labels{chosen.length ? ` (${chosen.length})` : ""}
          </Button>
        </div>
      )}
    </>
  );
}
